/**
 * Utility functions for event management
 */

/**
 * Check if an event is in the past (ended before today)
 * @param {Object} event - Event object with end_date
 * @returns {boolean} - True if event is in the past
 */
export const isPastEvent = (event) => {
  if (!event?.end_date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day

  const eventEndDate = new Date(event.end_date);
  eventEndDate.setHours(0, 0, 0, 0); // Reset time to start of day

  return eventEndDate < today;
};

/**
 * Check if an event is current (happening today)
 * @param {Object} event - Event object with start_date and end_date
 * @returns {boolean} - True if event is current
 */
export const isCurrentEvent = (event) => {
  if (!event?.start_date || !event?.end_date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventStartDate = new Date(event.start_date);
  eventStartDate.setHours(0, 0, 0, 0);

  const eventEndDate = new Date(event.end_date);
  eventEndDate.setHours(0, 0, 0, 0);

  return eventStartDate <= today && eventEndDate >= today;
};

/**
 * Check if an event is upcoming (starts after today)
 * @param {Object} event - Event object with start_date
 * @returns {boolean} - True if event is upcoming
 */
export const isUpcomingEvent = (event) => {
  if (!event?.start_date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventStartDate = new Date(event.start_date);
  eventStartDate.setHours(0, 0, 0, 0);

  return eventStartDate > today;
};

/**
 * Get the event type (past, current, upcoming)
 * @param {Object} event - Event object
 * @returns {string} - Event type
 */
export const getEventType = (event) => {
  if (isPastEvent(event)) return "past";
  if (isCurrentEvent(event)) return "current";
  if (isUpcomingEvent(event)) return "upcoming";
  return "unknown";
};

/**
 * Format date for display
 * @param {string} dateString - Date string
 * @returns {string} - Formatted date
 */
export const formatEventDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Get days until event starts
 * @param {Object} event - Event object with start_date
 * @returns {number} - Days until event starts (negative for past events)
 */
export const getDaysUntilEvent = (event) => {
  if (!event?.start_date) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventStartDate = new Date(event.start_date);
  eventStartDate.setHours(0, 0, 0, 0);

  const diffTime = eventStartDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
